'use client';

import Link, { type LinkProps } from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { forwardRef, type MouseEvent, type PropsWithChildren, useCallback } from 'react';
import { checkAndTriggerNavigationBlocker } from '@/providers/NavigationBlocker';
import { type BlockerLocation, checkAndTriggerBlocker } from '@/providers/RouterShim';
import { resolveRelativePath } from '@/utils/resolveRelativePath';

/**
 * RelativeLink wraps Next.js Link and automatically resolves relative paths.
 * Next.js Link does not handle relative paths like "./" or "../", so this
 * component provides that functionality for compatibility with React Router DOM.
 *
 * Also accepts `to` as an alias for `href` for react-router-dom compatibility.
 *
 * This component also integrates with the RouterShim blocker system to
 * intercept navigation when there are unsaved changes.
 */

export type RelativeLinkProps = PropsWithChildren<
  Omit<LinkProps, 'href' | 'as'> &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
      href?: LinkProps['href'];
      to?: string; // react-router-dom compatibility
      // Note: Next.js 'as' prop for URL masking is omitted to avoid conflict with styled-components
    }
>;

export const RelativeLink = forwardRef<HTMLAnchorElement, RelativeLinkProps>(
  ({ href, to, children, onClick, ...props }, ref) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // Support both `href` (Next.js) and `to` (react-router-dom)
    const targetHref = href ?? to ?? '';
    const resolvedHref =
      typeof targetHref === 'string' ? resolveRelativePath(targetHref, pathname) : targetHref;

    const handleClick = useCallback(
      (e: MouseEvent<HTMLAnchorElement>) => {
        // Allow custom onClick handlers
        onClick?.(e);

        // If already prevented, don't check blockers
        if (e.defaultPrevented) {
          return;
        }

        // Build current location
        const currentLocation: BlockerLocation = {
          pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
          hash: typeof window !== 'undefined' ? window.location.hash : '',
        };

        // Parse resolved href to get next location
        const resolvedPath =
          typeof resolvedHref === 'string' ? resolvedHref : (resolvedHref.pathname ?? '');
        const [nextPathname, rest] = resolvedPath.split('?');
        const [nextSearch, nextHash] = (rest || '').split('#');
        const nextLocation: BlockerLocation = {
          pathname: nextPathname,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: nextHash ? `#${nextHash}` : '',
        };

        // Check for RouterShim blockers (legacy)
        const isBlockedByRouterShim = checkAndTriggerBlocker(
          currentLocation,
          nextLocation,
          resolvedPath,
        );
        if (isBlockedByRouterShim) {
          e.preventDefault();
          return;
        }

        // Check for NavigationBlocker blockers (new system)
        const isBlockedByNavigationBlocker = checkAndTriggerNavigationBlocker(
          { pathname: currentLocation.pathname },
          { pathname: nextLocation.pathname },
          resolvedPath,
        );
        if (isBlockedByNavigationBlocker) {
          e.preventDefault();
        }
      },
      [onClick, pathname, searchParams, resolvedHref],
    );

    return (
      <Link ref={ref} href={resolvedHref} onClick={handleClick} {...props}>
        {children}
      </Link>
    );
  },
);

RelativeLink.displayName = 'RelativeLink';
